import { ReactElement, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import useDebounce from '@calm-web/use-debounce';
import { OnChange } from '@calm-web/use-form';

import { useAnalytics } from '@/hooks/analytics';
import { Filter, useSearchCategories } from '@/hooks/app/useSearch';
import SearchIcon from 'icons/search-icon.svg';

import messages from './messages';
import SearchBody from './SearchBody';
import SearchSuggestions from './SearchSuggestions';
import {
	CategoryAndDurationContainer,
	ClearLink,
	Form,
	FormInputStyled,
	SearchContainer,
	SearchInputContainer,
	SearchSubmitButton,
	StyledFormMultiSelect,
} from './styles';

function getOptionsByFilter(filterId: string, filters?: Filter[], excludeLabels: string[] = []) {
	const section = filters?.find(section => section.id === filterId);
	if (!section || !section.options) return [];

	return section.options
		.filter(option => !excludeLabels.includes(option.id))
		.map(option => ({ label: option.id, value: option.title }));
}

interface Option {
	label: string;
	value: string;
}

function findValue(labels: string[], options: Option[]) {
	return options.filter(option => labels.includes(option.label)).map(option => option.value);
}

const MAX_RECENT_SEARCHES = 5;

export default function SearchBar(): ReactElement {
	const [query, setQuery] = useState('');
	const [input, setInput] = useState('');
	const [selectedCategory, setCategory] = useState<string[]>([]);
	const [selectedDuration, setDuration] = useState<string[]>([]);
	const debouncedInput = useDebounce(input, 300);
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const { filters } = useSearchCategories();
	const [isTyping, setIsTyping] = useState(true);
	const [recentSearches, setRecentSearches] = useState<string[]>([]);
	const location = 'app';

	useEffect(() => {
		if (selectedCategory.length || selectedDuration.length) {
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setIsTyping(false);
		}
	}, [selectedCategory, selectedDuration]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const storedSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setRecentSearches(storedSearches);
		}
	}, []);

	const onSearchChange: OnChange = e => {
		setInput(e.target.value);
		setIsTyping(true);
	};

	const categoryOptions = getOptionsByFilter('category', filters, [
		'narrators_artists',
		'collections',
		'breathe',
	]);
	const durationOptions = getOptionsByFilter('duration', filters);

	const onClearClick = () => {
		setInput('');
		setQuery('');
		setCategory([]);
		setDuration([]);
		setIsTyping(true);

		logEvent({
			eventName: 'Search : Clear All : Clicked',
			eventProps: {
				filter_category: selectedCategory,
				filter_duration: selectedDuration,
				query: query,
				query_character_count: query.length,
				location,
			},
		});
	};

	const onSubmitQuery = (inputOverride?: string) => {
		const _input = inputOverride ?? input;

		if (_input) {
			setIsTyping(false);
		}
		setInput(_input);
		setQuery(_input);

		// Update recent searches when the search is submitted
		if (_input.trim()) {
			const recentSearchesArray = JSON.parse(localStorage.getItem('recentSearches') || '[]');
			const updatedSearches = [
				_input,
				...recentSearchesArray.filter((term: string) => term !== _input),
			].slice(0, MAX_RECENT_SEARCHES);
			localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
			setRecentSearches(updatedSearches);
		}
	};

	const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		onSubmitQuery();

		logEvent({
			eventName: 'Search : Query : Submitted',
			eventProps: {
				filter_category: selectedCategory,
				filter_duration: selectedDuration,
				query: query,
				query_character_count: query.length,
				location,
			},
		});
	};

	function logCategoryDurationChange(filter: string, newOptions: string[], selectedOption?: string) {
		logEvent({
			eventName: 'Filter Selector : Option: Added',
			eventProps: {
				is_multi_select: true,
				selected_options: newOptions,
				option: [selectedOption ?? null],
				filter_id: filter,
				location,
			},
		});
	}

	function onSetCategory(newCategories: string[], value?: string) {
		setCategory(newCategories);
		logCategoryDurationChange('category', newCategories, value);
	}

	function onSetDuration(newDuration: string[], value?: string) {
		setDuration(newDuration);
		logCategoryDurationChange('duration', newDuration, value);
	}

	return (
		<SearchContainer>
			<Form onSubmit={onSubmitForm}>
				<SearchInputContainer>
					<FormInputStyled
						label="Search"
						onChange={onSearchChange}
						name="search"
						value={input}
						Icon={SearchIcon}
						backgroundColor={'blackAlpha20'}
						textColor={'white'}
						noValidation
						noMargin
						autoComplete="off"
						onFocus={() => setIsTyping(true)}
					/>

					<SearchSubmitButton backgroundColor="white" onPress={() => onSubmitQuery()}>
						{formatMessage(messages.searchSubmit)}
					</SearchSubmitButton>
				</SearchInputContainer>
			</Form>
			<CategoryAndDurationContainer>
				{categoryOptions.length > 0 && (
					<StyledFormMultiSelect
						name="category"
						aria-label="category"
						textColor={selectedCategory.length > 0 ? 'gray7' : 'white'}
						backgroundColor={selectedCategory.length > 0 ? 'white' : 'blackAlpha20'}
						placeholder={formatMessage(messages.searchCategory)}
						state={selectedCategory}
						options={categoryOptions}
						onChange={onSetCategory}
					/>
				)}
				{durationOptions.length > 0 && (
					<StyledFormMultiSelect
						name="duration"
						aria-label="duration"
						textColor={selectedDuration.length > 0 ? 'gray7' : 'white'}
						backgroundColor={selectedDuration.length > 0 ? 'white' : 'blackAlpha20'}
						placeholder={formatMessage(messages.searchTime)}
						state={selectedDuration}
						options={durationOptions}
						onChange={onSetDuration}
					/>
				)}
				{(input.trim() !== '' || selectedDuration.length > 0 || selectedCategory.length > 0) && (
					<ClearLink onClick={onClearClick}> {formatMessage(messages.searchClear)} </ClearLink>
				)}
			</CategoryAndDurationContainer>
			{isTyping ? (
				<SearchSuggestions
					onSubmitQuery={onSubmitQuery}
					debouncedInput={debouncedInput}
					input={input}
					recentSearches={recentSearches}
					location={location}
				/>
			) : (
				<SearchBody
					selectedCategory={selectedCategory}
					selectedCategoryValue={findValue(selectedCategory, categoryOptions)}
					selectedDuration={selectedDuration}
					selectedDurationValue={findValue(selectedDuration, durationOptions)}
					query={query}
				/>
			)}
		</SearchContainer>
	);
}
